/**
 * How the Dale dot moves: springs toward what its mood asks for, and a speech
 * plan that times each word so the dots pulse with what it says.
 */

export type OrbMood = 'idle' | 'listening' | 'thinking' | 'speaking';

export type SpringState = { readonly value: number; readonly velocity: number };

export type MoodTarget = {
  /** Radians per second around the tilted axis. */
  spin: number;
  /** 0 is a whole sphere; 1 is the dust cloud it dissolves into while thinking. */
  scatter: number;
  /** How much the sphere breathes in and out. */
  breathe: number;
};

export const MOOD_TARGETS: Readonly<Record<OrbMood, MoodTarget>> = {
  idle: { spin: 0.22, scatter: 0, breathe: 0.018 },
  listening: { spin: 0.55, scatter: 0, breathe: 0.024 },
  thinking: { spin: 0.9, scatter: 1, breathe: 0.01 },
  speaking: { spin: 0.34, scatter: 0, breathe: 0.012 },
};

const STIFFNESS = 90;
const DAMPING = 16;
/** Longer frames are cut into steps this small, so a tab coming back does not fling the dots. */
const MAX_STEP_S = 1 / 60;

/** One semi-implicit Euler step of a critically-damped-ish spring. */
export function stepSpring(state: SpringState, target: number, dtSeconds: number): SpringState {
  let value = state.value;
  let velocity = state.velocity;
  let remaining = Math.min(dtSeconds, 0.25);
  while (remaining > 0) {
    const dt = Math.min(remaining, MAX_STEP_S);
    velocity += (STIFFNESS * (target - value) - DAMPING * velocity) * dt;
    value += velocity * dt;
    remaining -= dt;
  }
  return { value, velocity };
}

/** Spanish speech runs about six or seven syllables a second. */
export const SYLLABLE_MS = 150;
const COMMA_PAUSE_MS = 180;
const STOP_PAUSE_MS = 340;
const VOWEL_GROUP = /[aeiouáéíóúü]+/gi;

export function countSyllables(word: string): number {
  return Math.max(1, word.match(VOWEL_GROUP)?.length ?? 0);
}

export type SpeechWord = { readonly word: string; readonly atMs: number; readonly syllables: number };
export type SpeechPlan = {
  readonly words: readonly SpeechWord[];
  readonly durationMs: number;
  /** How long one syllable lasts in this plan; a plan fitted to a recording has its own pace. */
  readonly syllableMs?: number;
};

export function speechPlan(text: string): SpeechPlan {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  let clock = 0;
  const words = tokens.map((word) => {
    const syllables = countSyllables(word);
    const entry = { word, atMs: clock, syllables };
    clock += syllables * SYLLABLE_MS + pauseAfter(word);
    return entry;
  });
  return { words, durationMs: clock };
}

/**
 * The same words laid over a recording: the estimate keeps the order and the
 * pauses, the recording sets the length, so the text follows the voice. The
 * last word ends where the recording does; the estimate's trailing pause goes.
 */
export function fitSpeechPlan(plan: SpeechPlan, durationMs: number): SpeechPlan {
  const last = plan.words[plan.words.length - 1];
  if (!last || durationMs <= 0) return plan;
  const pace = plan.syllableMs ?? SYLLABLE_MS;
  const scale = durationMs / (last.atMs + last.syllables * pace);
  return {
    words: plan.words.map((word) => ({ ...word, atMs: word.atMs * scale })),
    durationMs,
    syllableMs: pace * scale,
  };
}

function pauseAfter(word: string): number {
  if (/[.!?…:]$/.test(word)) return STOP_PAUSE_MS;
  if (/[,;]$/.test(word)) return COMMA_PAUSE_MS;
  return 0;
}

/** How many words are on screen `elapsedMs` into the plan. */
export function wordsShownAt(plan: SpeechPlan, elapsedMs: number): number {
  if (elapsedMs < 0) return 0;
  return plan.words.filter((word) => word.atMs <= elapsedMs).length;
}

/** 0 to 1: a soft bump per syllable while a word is being said, silence in the pauses. */
export function speechLevelAt(plan: SpeechPlan, elapsedMs: number): number {
  if (elapsedMs < 0) return 0;
  const word = [...plan.words].reverse().find((entry) => entry.atMs <= elapsedMs);
  if (!word) return 0;
  const into = elapsedMs - word.atMs;
  const pace = plan.syllableMs ?? SYLLABLE_MS;
  const length = word.syllables * pace;
  if (into >= length) return 0;
  const syllablePhase = (into % pace) / pace;
  return 0.35 + 0.65 * Math.sin(syllablePhase * Math.PI);
}
